import * as React from 'react';
import type { ColDef, GridApi, CellContextMenuEvent, ColumnResizedEvent, ColumnMovedEvent, ColumnState } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import {
	Pencil,
	Trash2,
	Copy,
	ExternalLink,
	MoreVertical,
	FileSpreadsheet,
	FileDown,
	PlusCircle,
	RotateCcw
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

import { SearchInput } from '@/components/ui/search-input';
import { RefreshButton } from '@/components/ui/refresh-button';
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuSeparator,
	DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';
import { exportToCSV, exportToExcel } from '@/lib/export-utils';

import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-quartz.css';

const DATE_COLUMN_KEYWORDS = [
	'date',
	'expire',
	'expiry',
	'start',
	'end',
	'created',
	'updated',
	'timestamp'
] as const;
const DATE_COLUMN_WIDTH = 120;

// Module-level constant to prevent ag-Grid from re-evaluating columns on every render.
// When defaultColDef is an inline object it creates a new reference each render,
// causing ag-Grid to re-process all column definitions and reset user-applied widths.
const DEFAULT_COL_DEF = {
	sortable: true,
	resizable: true,
	lockVisible: true
} as const;

// localStorage persistence for column state (widths, order, pinning, visibility)
const COLUMN_STATE_STORAGE_PREFIX = 'vectro-grid-col-state:';

function getColumnStateKey(gridId: string): string {
	return `${COLUMN_STATE_STORAGE_PREFIX}${gridId}`;
}

function saveColumnStateToStorage(gridId: string, api: GridApi): void {
	try {
		const state = api.getColumnState();
		localStorage.setItem(getColumnStateKey(gridId), JSON.stringify(state));
	} catch {
		// Silently fail if localStorage is full or unavailable
	}
}

function loadColumnStateFromStorage(gridId: string): ColumnState[] | null {
	try {
		const stored = localStorage.getItem(getColumnStateKey(gridId));
		if (stored) {
			return JSON.parse(stored) as ColumnState[];
		}
	} catch {
		// Silently fail on parse errors
	}
	return null;
}

function clearColumnStateFromStorage(gridId: string): void {
	try {
		localStorage.removeItem(getColumnStateKey(gridId));
	} catch {
		// Silently fail
	}
}

/**
 * Structurally compare two ColDef arrays to determine if they represent
 * the same column configuration. Only compares serializable properties
 * that affect column layout — functions (valueGetter, cellRenderer, etc.)
 * are intentionally excluded since they are recreated every render but
 * don't affect column structure or sizing.
 */
function areColumnDefsStructurallyEqual<TData>(
	a: ColDef<TData>[],
	b: ColDef<TData>[]
): boolean {
	if (a.length !== b.length) return false;
	for (let i = 0; i < a.length; i++) {
		const colA = a[i];
		const colB = b[i];
		if (
			colA.field !== colB.field ||
			colA.headerName !== colB.headerName ||
			colA.colId !== colB.colId ||
			colA.width !== colB.width ||
			colA.minWidth !== colB.minWidth ||
			colA.maxWidth !== colB.maxWidth ||
			colA.flex !== colB.flex ||
			colA.hide !== colB.hide ||
			colA.sortable !== colB.sortable ||
			colA.resizable !== colB.resizable ||
			colA.pinned !== colB.pinned ||
			colA.type !== colB.type
		) {
			return false;
		}
	}
	return true;
}

const isDateRelatedColumn = <TData,>(column: ColDef<TData>): boolean => {
	const matchesKeyword = (value?: string | null) => {
		if (!value) {
			return false;
		}

		const normalized = value.toLowerCase();
		return DATE_COLUMN_KEYWORDS.some(keyword =>
			normalized.includes(keyword)
		);
	};

	const columnType = column.type;
	if (Array.isArray(columnType)) {
		if (columnType.some(type => matchesKeyword(type))) {
			return true;
		}
	} else if (typeof columnType === 'string') {
		if (matchesKeyword(columnType)) {
			return true;
		}
	}

	if (typeof column.field === 'string' && matchesKeyword(column.field)) {
		return true;
	}

	if (column.colId != null && matchesKeyword(String(column.colId))) {
		return true;
	}

	if (typeof column.headerName === 'string') {
		return matchesKeyword(column.headerName);
	}

	return false;
};

interface DataTableProps<TData> {
	columns: ColDef<TData>[];
	data: TData[];
	title?: string;
	description?: string;
	showCreateButton?: boolean;
	showRefreshButton?: boolean;
	onCreate?: () => void;
	onRefresh?: () => void;
	isLoading?: boolean;
	searchPlaceholder?: string;
	showSearch?: boolean;
	onSearchValueChange?: (value: string) => void;
	clearSearchToken?: number;
	onRowClick?: (row: TData) => void;
	onRowDoubleClick?: (row: TData) => void;
	onDelete?: (row: TData) => void; // For context menu delete action
	onClone?: (row: TData) => void; // For context menu clone action
	onOpenInNewWindow?: (row: TData) => void; // For context menu open in new window action
	autoSelectFirst?: boolean;
	renderCustomFilters?: () => React.ReactNode;
	selectedRowId?: string | null; // New prop for external selection control
	getRowId?: (row: TData) => string; // Function to get unique ID from row data
	exportFilename?: string; // Filename for export (without extension)
	/** Stable unique identifier for this grid instance. Used as the localStorage key
	 *  for persisting column state (widths, order, pinning) across sessions.
	 *  Must be a constant kebab-case string, e.g. 'contracts-list'. */
	gridId: string;
}

export function DataTable<TData>({
	columns,
	data,
	title,
	description: _description,
	showCreateButton = true,
	showRefreshButton = true,
	onCreate,
	onRefresh,
	isLoading = false,
	searchPlaceholder = 'Search...',
	showSearch = true,
	onSearchValueChange,
	clearSearchToken,
	onRowClick,
	onRowDoubleClick,
	onDelete,
	onClone,
	onOpenInNewWindow,
	autoSelectFirst = true,
	renderCustomFilters,
	selectedRowId,
	getRowId,
	exportFilename,
	gridId
}: DataTableProps<TData>) {
	const { t } = useTranslation('common');
	const gridApiRef = React.useRef<GridApi<TData> | null>(null);
	const [searchValue, setSearchValue] = React.useState('');
	const hasAutoSelectedRef = React.useRef(false);
	const containerRef = React.useRef<HTMLDivElement | null>(null);
	const [focusedRowIndex, setFocusedRowIndex] = React.useState<number | null>(
		null
	);
	const updateFocusedIndex = React.useCallback((index: number | null) => {
		setFocusedRowIndex(index);
	}, []);
	const previousClearSearchTokenRef = React.useRef<number | undefined>(
		clearSearchToken
	);

	// Memoize the getRowId wrapper to prevent ag-Grid from re-processing on every render
	const stableGetRowId = React.useMemo(
		() =>
			getRowId
				? (params: { data: TData }) => getRowId(params.data)
				: undefined,
		[getRowId]
	);

	// Memoize onGridReady to prevent unnecessary ag-Grid option updates
	const handleGridReady = React.useCallback(
		(params: { api: GridApi<TData> }) => {
			gridApiRef.current = params.api;

			if (isLoading) {
				params.api.showLoadingOverlay();
			}

			// Restore saved column state from localStorage
			if (gridId) {
				const savedState = loadColumnStateFromStorage(gridId);
				if (savedState) {
					params.api.applyColumnState({
						state: savedState,
						applyOrder: true
					});
				}
			}
		},
		[isLoading, gridId]
	);

	// Persist column state to localStorage when user resizes or reorders columns
	const persistColumnState = React.useCallback(() => {
		if (!gridId) return;
		const api = gridApiRef.current;
		if (!api) return;
		saveColumnStateToStorage(gridId, api);
	}, [gridId]);

	const handleColumnResized = React.useCallback(
		(event: ColumnResizedEvent<TData>) => {
			if (event.finished) persistColumnState();
		},
		[persistColumnState]
	);

	const handleColumnMoved = React.useCallback(
		(event: ColumnMovedEvent<TData>) => {
			if (event.finished) persistColumnState();
		},
		[persistColumnState]
	);

	// Reset column layout to defaults and clear saved state
	const handleResetColumns = React.useCallback(() => {
		if (gridId) {
			clearColumnStateFromStorage(gridId);
		}
		const api = gridApiRef.current;
		if (api) {
			api.resetColumnState();
		}
	}, [gridId]);

	// Memoize onRowDoubleClicked to prevent unnecessary ag-Grid option updates
	const handleRowDoubleClicked = React.useCallback(
		(event: { data?: TData }) => {
			if (onRowDoubleClick && event.data) {
				onRowDoubleClick(event.data);
			}
		},
		[onRowDoubleClick]
	);

	React.useEffect(() => {
		onSearchValueChange?.(searchValue);
	}, [onSearchValueChange, searchValue]);

	React.useEffect(() => {
		if (clearSearchToken == null) {
			return;
		}

		if (previousClearSearchTokenRef.current === clearSearchToken) {
			return;
		}

		previousClearSearchTokenRef.current = clearSearchToken;
		setSearchValue('');

		const api = gridApiRef.current;
		if (api) {
			api.setGridOption('quickFilterText', '');
		}
	}, [clearSearchToken]);

	// Compute enhanced columns from the raw column definitions
	const computedColumns = React.useMemo(() => {
		return columns.map((column: ColDef<TData>, index: number) => {
			const columnDef: ColDef<TData> = {
				...column
			};
			const isDateColumn = isDateRelatedColumn(columnDef);

			// Enforce consistent width for all date-related columns (only when no explicit width is set)
			if (isDateColumn && columnDef.width == null) {
				columnDef.width = DATE_COLUMN_WIDTH;
				if ('flex' in columnDef) {
					delete columnDef.flex;
				}
			}

			if (index === 0 && columnDef.flex == null && columnDef.width == null && !isDateColumn) {
				columnDef.flex = 1;
			}

			return columnDef;
		});
	}, [columns]);

	// Stabilize the columnDefs reference passed to ag-Grid.
	// Parent components (list pages) often define columns as plain const arrays,
	// which creates new references on every render (e.g. from useLocation() re-renders).
	// ag-Grid uses reference equality to detect columnDefs changes and will call
	// setColumnDefs() which recreates ALL column objects from scratch, resetting
	// any user-applied column widths/sizes.
	// This ref ensures ag-Grid only sees a new reference when columns actually changed.
	const stableColumnsRef = React.useRef(computedColumns);
	if (!areColumnDefsStructurallyEqual(stableColumnsRef.current, computedColumns)) {
		stableColumnsRef.current = computedColumns;
	}
	const enhancedColumns = stableColumnsRef.current;

	// Context menu state
	const [contextMenuRow, setContextMenuRow] = React.useState<TData | null>(
		null
	);
	const [contextMenuPosition, setContextMenuPosition] = React.useState<{
		x: number;
		y: number;
	} | null>(null);

	// Handle cell context menu (right-click)
	const handleCellContextMenu = React.useCallback(
		(event: CellContextMenuEvent<TData>) => {
			const mouseEvent = event.event as MouseEvent;
			if (mouseEvent && event.data) {
				mouseEvent.preventDefault();
				setContextMenuRow(event.data);
				setContextMenuPosition({
					x: mouseEvent.clientX,
					y: mouseEvent.clientY
				});
			}
		},
		[]
	);

	// Close context menu
	const handleCloseContextMenu = React.useCallback(() => {
		setContextMenuPosition(null);
		setContextMenuRow(null);
	}, []);

	// Context menu actions
	const handleContextMenuEdit = React.useCallback(() => {
		if (contextMenuRow && onRowDoubleClick) {
			onRowDoubleClick(contextMenuRow);
		}
		handleCloseContextMenu();
	}, [contextMenuRow, onRowDoubleClick, handleCloseContextMenu]);

	const handleContextMenuDelete = React.useCallback(() => {
		if (contextMenuRow && onDelete) {
			onDelete(contextMenuRow);
		}
		handleCloseContextMenu();
	}, [contextMenuRow, onDelete, handleCloseContextMenu]);

	const handleContextMenuClone = React.useCallback(() => {
		if (contextMenuRow && onClone) {
			onClone(contextMenuRow);
		}
		handleCloseContextMenu();
	}, [contextMenuRow, onClone, handleCloseContextMenu]);

	const handleContextMenuOpenInNewWindow = React.useCallback(() => {
		if (contextMenuRow && onOpenInNewWindow) {
			onOpenInNewWindow(contextMenuRow);
		}
		handleCloseContextMenu();
	}, [contextMenuRow, onOpenInNewWindow, handleCloseContextMenu]);

	// Export handlers
	const getExportFilename = React.useCallback(() => {
		if (exportFilename) {
			return exportFilename;
		}
		// Generate filename from title or use default
		if (title) {
			return title.toLowerCase().replace(/\s+/g, '-');
		}
		return 'export';
	}, [exportFilename, title]);

	const handleExportCSV = React.useCallback(() => {
		const api = gridApiRef.current;
		if (!api) {
			return;
		}
		exportToCSV(api, columns, { filename: getExportFilename() });
	}, [columns, getExportFilename]);

	const handleExportExcel = React.useCallback(() => {
		const api = gridApiRef.current;
		if (!api) {
			return;
		}
		exportToExcel(api, columns, {
			filename: getExportFilename(),
			sheetName: title || 'Data'
		});
	}, [columns, getExportFilename, title]);

	const handleSearchChange = React.useCallback(
		(event: React.ChangeEvent<HTMLInputElement>) => {
			const value = event.target.value;
			setSearchValue(value);

			const api = gridApiRef.current;
			if (!api) {
				return;
			}

			api.setGridOption('quickFilterText', value);
		},
		[]
	);

	const handleSearchKeyDown = React.useCallback(
		(event: React.KeyboardEvent<HTMLInputElement>) => {
			if (event.key !== 'Escape') {
				return;
			}

			event.preventDefault();
			event.stopPropagation();
			setSearchValue('');

			const api = gridApiRef.current;

			if (!api) {
				return;
			}

			api.setGridOption('quickFilterText', '');
		},
		[]
	);

	React.useEffect(() => {
		const api = gridApiRef.current;

		if (!api) {
			return;
		}

		if (isLoading && (!data || data.length === 0)) {
			// Only show loading overlay on initial load, not during filter changes
			// This prevents interrupting AG Grid row animations
			api.showLoadingOverlay();
			return;
		}

		if (!data || data.length === 0) {
			api.showNoRowsOverlay();
			return;
		}

		api.hideOverlay();

		// Auto-select the first row when data is loaded for the first time
		if (autoSelectFirst && data.length > 0 && !hasAutoSelectedRef.current) {
			// Use setTimeout to ensure the grid is fully rendered before selection
			setTimeout(() => {
				const firstNode = api.getRowNode('0');
				if (firstNode) {
					firstNode.setSelected(true);
					hasAutoSelectedRef.current = true;
					updateFocusedIndex(0);
					// Trigger onRowClick if provided and we have data
					if (onRowClick && data[0]) {
						onRowClick(data[0]);
					}
				}
			}, 0);
		}
	}, [isLoading, data, onRowClick, autoSelectFirst]);

	// Reset auto-selection flag when data is empty (e.g., on refresh/filter)
	React.useEffect(() => {
		if (!data || data.length === 0) {
			hasAutoSelectedRef.current = false;
		}
	}, [data]);

	// Focus the grid container when route changes to this list screen
	const location = useLocation();
	React.useEffect(() => {
		// Reset auto-selection when navigating to this screen
		// This ensures first row is selected when returning to the list
		hasAutoSelectedRef.current = false;

		// Use a small delay to ensure the grid is rendered
		// Only focus container on route change, not on data updates (to preserve input focus)
		const timeoutId = setTimeout(() => {
			// Only focus if no other element is currently focused (or body is focused)
			const activeElement = document.activeElement;
			const shouldFocus =
				!activeElement ||
				activeElement === document.body ||
				activeElement.tagName === 'BODY';

			if (shouldFocus) {
				containerRef.current?.focus();
			}
		}, 100);
		return () => clearTimeout(timeoutId);
	}, [location.pathname]);

	// Handle auto-selection when data changes (separate from focus logic)
	React.useEffect(() => {
		const api = gridApiRef.current;
		if (!api || !autoSelectFirst || !data || data.length === 0) {
			return;
		}

		// Auto-select first row if no selection exists
		const selectedNodes = api.getSelectedNodes();
		if (selectedNodes.length === 0 && !hasAutoSelectedRef.current) {
			const timeoutId = setTimeout(() => {
				const firstNode = api.getDisplayedRowAtIndex(0);
				if (firstNode) {
					firstNode.setSelected(true);
					hasAutoSelectedRef.current = true;
					updateFocusedIndex(0);
					if (onRowClick && firstNode.data) {
						onRowClick(firstNode.data);
					}
				}
			}, 0);
			return () => clearTimeout(timeoutId);
		}
	}, [autoSelectFirst, data, onRowClick, updateFocusedIndex]);

	// Handle external selection changes
	// Only sync selection to grid when selectedRowId actually changes
	// We track the previous value to avoid clearing selection on data array reference changes
	const previousSelectedRowIdRef = React.useRef<string | null | undefined>(
		null
	);

	React.useEffect(() => {
		const api = gridApiRef.current;
		if (!api || !getRowId || !data || data.length === 0) {
			return;
		}

		if (!selectedRowId) {
			updateFocusedIndex(null);
			return;
		}

		// Only update selection if selectedRowId actually changed (not just data array)
		// Note: We use null as initial value, so first meaningful selection will always run
		const selectionChanged =
			previousSelectedRowIdRef.current !== selectedRowId;
		if (!selectionChanged) {
			return;
		}
		previousSelectedRowIdRef.current = selectedRowId;

		// Clear all selections first
		api.deselectAll();

		// If we have a selectedRowId, find and select that row
		if (selectedRowId) {
			// Use getRowId to find the correct row node
			const rowNode = api.getRowNode(selectedRowId);
			if (rowNode) {
				rowNode.setSelected(true);
				// Ensure the selected row is visible
				api.ensureNodeVisible(rowNode);
			}
		}
	}, [selectedRowId, data, getRowId, updateFocusedIndex]);

	const handleRowClicked = React.useCallback(
		(event: { data?: TData; rowIndex: number | null }) => {
			if (onRowClick && event.data) {
				onRowClick(event.data);
			}
			if (event.rowIndex != null) {
				updateFocusedIndex(event.rowIndex);
			}
		},
		[onRowClick, updateFocusedIndex]
	);

	const moveSelection = React.useCallback(
		(direction: 1 | -1) => {
			const api = gridApiRef.current;
			if (!api) {
				return;
			}

			const rowCount = api.getDisplayedRowCount();
			if (rowCount === 0) {
				return;
			}

			let currentIndex =
				focusedRowIndex != null
					? focusedRowIndex
					: direction > 0
						? -1
						: rowCount;
			const nextIndex = Math.max(
				0,
				Math.min(rowCount - 1, currentIndex + direction)
			);

			const node = api.getDisplayedRowAtIndex(nextIndex);
			if (!node || !node.data) {
				return;
			}

			node.setSelected(true);
			api.ensureIndexVisible(nextIndex);
			updateFocusedIndex(nextIndex);

			if (onRowClick) {
				onRowClick(node.data);
			}
		},
		[focusedRowIndex, onRowClick, updateFocusedIndex]
	);

	const handleKeyDown = React.useCallback(
		(event: React.KeyboardEvent<HTMLDivElement>) => {
			if (event.altKey || event.ctrlKey || event.metaKey) {
				return;
			}

			const target = event.target as HTMLElement;
			if (
				target &&
				['INPUT', 'TEXTAREA', 'SELECT', 'BUTTON'].includes(
					target.tagName
				)
			) {
				return;
			}

			if (event.key === 'ArrowDown') {
				event.preventDefault();
				moveSelection(1);
			} else if (event.key === 'ArrowUp') {
				event.preventDefault();
				moveSelection(-1);
			} else if (event.key === 'Enter' && onRowDoubleClick) {
				event.preventDefault();
				const api = gridApiRef.current;
				if (!api) {
					return;
				}

				// Get the currently selected row
				const selectedNodes = api.getSelectedNodes();
				if (selectedNodes.length > 0 && selectedNodes[0].data) {
					onRowDoubleClick(selectedNodes[0].data);
				}
			}
		},
		[moveSelection, onRowDoubleClick]
	);

	return (
		<div
			ref={containerRef}
			tabIndex={0}
			onKeyDown={handleKeyDown}
			className="flex flex-col w-full h-full space-y-3 outline-none focus:outline-none"
		>
			{/* Toolbar */}
			<div className="flex items-center gap-3 px-4 pt-4">
				{showSearch && (
					<SearchInput
						placeholder={searchPlaceholder}
						value={searchValue}
						onChange={handleSearchChange}
						onKeyDown={handleSearchKeyDown}
						variant="transparent"
						className="max-w-sm flex-1 min-w-0"
					/>
				)}
				{renderCustomFilters && renderCustomFilters()}
				<div className="flex items-center gap-1 ml-auto flex-shrink-0">
					{showCreateButton && onCreate && (
						<>
							<button
								type="button"
								onClick={onCreate}
								className="inline-flex items-center justify-center rounded-md h-9 w-9 text-muted-foreground transition-colors hover:text-foreground hover:bg-accent focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
								title={t('buttons.addNew')}
								aria-label={t('buttons.addNew')}
							>
								<PlusCircle className="h-5 w-5" strokeWidth={2.25} />
							</button>
							<div className="w-px h-5 bg-border mx-1" />
						</>
					)}
					{showRefreshButton && (
						<RefreshButton
							onRefresh={onRefresh}
							isLoading={isLoading}
							showLabel={false}
							variant="ghost"
							size="icon"
							className="h-9 w-9 text-muted-foreground hover:text-foreground"
						/>
					)}
					{/* Options Menu */}
					<DropdownMenu>
						<DropdownMenuTrigger className="inline-flex items-center justify-center rounded-md h-9 w-9 text-muted-foreground transition-colors hover:text-foreground hover:bg-accent focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring">
							<MoreVertical className="h-4 w-4" />
							<span className="sr-only">
								{t('buttons.options')}
							</span>
						</DropdownMenuTrigger>
						<DropdownMenuContent align="end" className="w-48">
							<DropdownMenuItem onClick={handleExportExcel}>
								<FileSpreadsheet className="mr-2 h-4 w-4" />
								{t('export.toExcel')}
							</DropdownMenuItem>
							<DropdownMenuItem onClick={handleExportCSV}>
								<FileDown className="mr-2 h-4 w-4" />
								{t('export.toCSV')}
							</DropdownMenuItem>						{gridId && (
							<>
								<DropdownMenuSeparator />
								<DropdownMenuItem onClick={handleResetColumns}>
									<RotateCcw className="mr-2 h-4 w-4" />
									{t('export.resetColumns')}
								</DropdownMenuItem>
							</>
						)}						</DropdownMenuContent>
					</DropdownMenu>
				</div>
			</div>

			{/* Table */}
			<div className="flex flex-col flex-1 overflow-hidden">
				<div
					className="flex-1 overflow-hidden ag-theme-quartz vectro-ag-theme"
					onContextMenu={e => e.preventDefault()}
				>
					<AgGridReact<TData>
						theme="legacy"
						rowData={data}
						columnDefs={enhancedColumns}
						rowHeight={50}
						headerHeight={40}
						defaultColDef={DEFAULT_COL_DEF}
						animateRows={true}
						getRowId={stableGetRowId}
						suppressPaginationPanel
						suppressCellFocus
						rowSelection="single"
						suppressContextMenu={true}
						onCellContextMenu={handleCellContextMenu}
						overlayLoadingTemplate="<div class='ag-custom-overlay'><div class='ag-custom-overlay__title'>Loading...</div></div>"
						overlayNoRowsTemplate="<div class='ag-custom-overlay'><div class='ag-custom-overlay__title'>No results</div><div class='ag-custom-overlay__subtitle'>Try adjusting your search or data source.</div></div>"
						onGridReady={handleGridReady}
						onRowClicked={handleRowClicked}
						onRowDoubleClicked={handleRowDoubleClicked}
						onColumnResized={handleColumnResized}
						onColumnMoved={handleColumnMoved}
					/>
				</div>
			</div>

			{/* Custom Context Menu */}
			{contextMenuPosition && contextMenuRow && (
				<div
					style={{
						position: 'fixed',
						top: contextMenuPosition.y,
						left: contextMenuPosition.x,
						zIndex: 9999
					}}
				>
					<DropdownMenu
						open={true}
						onOpenChange={(open: boolean) =>
							!open && handleCloseContextMenu()
						}
					>
						<DropdownMenuTrigger asChild>
							<div style={{ width: 1, height: 1 }} />
						</DropdownMenuTrigger>
						<DropdownMenuContent
							className="min-w-[160px]"
							onEscapeKeyDown={handleCloseContextMenu}
							onInteractOutside={handleCloseContextMenu}
						>
							{onOpenInNewWindow && (
								<>
									<DropdownMenuItem
										onClick={handleContextMenuOpenInNewWindow}
									>
										<ExternalLink className="mr-2 h-4 w-4" />
										{t('buttons.openInNewWindow')}
									</DropdownMenuItem>
									<DropdownMenuSeparator />
								</>
							)}
							{onRowDoubleClick && (
								<DropdownMenuItem
									onClick={handleContextMenuEdit}
								>
									<Pencil className="mr-2 h-4 w-4" />
									{t('buttons.edit')}
								</DropdownMenuItem>
							)}
							{onClone && (
								<DropdownMenuItem
									onClick={handleContextMenuClone}
								>
									<Copy className="mr-2 h-4 w-4" />
									{t('buttons.clone')}
								</DropdownMenuItem>
							)}
							{onDelete && (
								<>
									<DropdownMenuSeparator />
									<DropdownMenuItem
										variant="destructive"
										onClick={handleContextMenuDelete}
									>
										<Trash2 className="mr-2 h-4 w-4" />
										{t('buttons.delete')}
									</DropdownMenuItem>
								</>
							)}
						</DropdownMenuContent>
					</DropdownMenu>
				</div>
			)}
		</div>
	);
}
